import { css } from '@emotion/react';
import { Accordion, AccordionItem } from '@spark-web/accordion';
import { Box } from '@spark-web/box';
import { Button } from '@spark-web/button';
import { Checkbox } from '@spark-web/checkbox';
import { Combobox } from '@spark-web/combobox';
import { Divider } from '@spark-web/divider';
import { Field } from '@spark-web/field';
import { MultiSelect } from '@spark-web/multi-select';
import { Stack } from '@spark-web/stack';
import { Text } from '@spark-web/text';
import { useTheme } from '@spark-web/theme';
import { ChangeEvent, FC, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { Alert } from '~/components/common/alert';
import { FilterIcon } from '~/components/common/icon';
import { ASSET_URL, CHANNEL_REQUIREMENTS } from '~/constants';
import { Postcode, postcodes } from '~/constants/postcodes';
import { Promotion } from '~/gql/graphql';
import { useModal } from '~/hooks/useModal';
import {
  MarketplaceCategories,
  MarketplaceChannel,
  MarketplaceManufacturerBrands,
  MarketplacePartner,
} from '~/types/marketplace';

import { Button as BrighteButton } from '../../common/button';

import { FilterSortBaseDialog } from './filter-sort-base-dialog';

const CATEGORY_ICON_SIZE = 24;
const POSTCODE_SUGGESTION_LIMIT = 50;

const buildPostcodeInput = (postcode: Postcode) => {
  let str = postcode.postCode;

  if (postcode.suburb) {
    str = `${postcode.suburb} ${str}`;
  }

  return str;
};

export type FilterFormData = {
  postcode?: {
    suburb: string;
    postCode: string;
    state: string;
  };
  manufacturerBrands: Array<string>;
  promotions: Array<string>;
  categories: Array<string>;
};

export type FilterDialogProps = {
  initialValues?: FilterFormData;
  categories?: Array<MarketplaceCategories>;
  promotions?: Array<Pick<Promotion, 'title' | 'code'>>;
  manufacturerBrands?: Array<MarketplaceManufacturerBrands>;
  channel: MarketplaceChannel | null;
  partner?: MarketplacePartner;
  status?: string;
  onFilter?: (filters: FilterFormData) => void;
};

export const FilterDialog: FC<FilterDialogProps> = ({
  initialValues,
  categories,
  manufacturerBrands,
  promotions = [],
  channel,
  partner,
  status,
  onFilter,
}) => {
  const theme = useTheme();
  const { isOpen, open, close, ref: triggerRef } = useModal('filter-vendors');

  const fixedPostcode = channel ? CHANNEL_REQUIREMENTS[channel]?.fixedPostcode : null;

  const { control, handleSubmit } = useForm<FilterFormData>({
    defaultValues: {
      postcode: initialValues?.postcode || undefined,
      manufacturerBrands: initialValues?.manufacturerBrands ?? [],
      promotions: initialValues?.promotions ?? [],
      categories: initialValues?.categories ?? [],
    },
  });

  const defaultPostcodeInput = initialValues?.postcode ? buildPostcodeInput(initialValues?.postcode) : '';
  const [postcodeInput, setPostcodeInput] = useState(defaultPostcodeInput);

  const postcodeOptions = useMemo(() => {
    const searchTerm = postcodeInput?.trim();

    if (!searchTerm || searchTerm.length < 2) return [];

    const lowerSearchTerm = searchTerm.toLowerCase();

    return postcodes
      .filter((postcode) => {
        const suburb = postcode.suburb?.toLowerCase() || '';
        const postCode = postcode.postCode?.toLowerCase() || '';

        return (
          suburb.includes(lowerSearchTerm) ||
          postCode.includes(lowerSearchTerm) ||
          `${suburb} ${postCode}`.includes(lowerSearchTerm)
        );
      })
      .slice(0, POSTCODE_SUGGESTION_LIMIT);
  }, [postcodeInput]);

  const manufacturerOptions = useMemo(() => {
    return [
      {
        label: '',
        options: manufacturerBrands
          ? manufacturerBrands.map((manufacturer) => ({ label: manufacturer.name, value: manufacturer.name }))
          : [],
      },
    ];
  }, [manufacturerBrands]);

  const onDialogCloseAutoFocus = (event: Event) => {
    event.preventDefault();
    triggerRef.current?.focus();
  };

  const onPromotionCheckboxChange =
    (promotionCode: string, currentPromotions: Array<string>, onChange: (value: Array<string>) => void) =>
    (event: ChangeEvent<HTMLInputElement>) => {
      if (event.target.checked) {
        onChange([...currentPromotions, promotionCode]);
      } else {
        onChange(currentPromotions.filter((code) => code !== promotionCode));
      }
    };

  const onCategoryCheckboxChange =
    (subCategorySlug: string, currentCategories: Array<string>, onChange: (value: Array<string>) => void) =>
    (event: ChangeEvent<HTMLInputElement>) => {
      if (event.target.checked) {
        onChange([...currentCategories, subCategorySlug]);
      } else {
        onChange(currentCategories.filter((slug) => slug !== subCategorySlug));
      }
    };

  const onSubmit = (data: FilterFormData) => {
    onFilter?.(data);
    close();
  };

  return (
    <>
      <Button
        data={{ testId: 'filter-button' }}
        tone="neutral"
        onClick={open}
        rounded
        size="large"
        ref={triggerRef}
        css={css({
          height: 48,
          width: '100%',
          alignSelf: 'center',
          [`@media (min-width: ${theme.breakpoint.tablet}px)`]: {
            width: 156,
          },
        })}
      >
        Filters <FilterIcon partner={partner} brighteSize="xsmall" />
      </Button>
      <FilterSortBaseDialog
        isOpen={isOpen}
        onToggle={close}
        onCloseAutoFocus={onDialogCloseAutoFocus}
        title="Filter your results"
        formId="filter-form"
        onSubmit={handleSubmit(onSubmit)}
      >
        <Box gap="xxlarge">
          {!fixedPostcode ? (
            <Stack gap="large" paddingBottom="xlarge">
              <Text size="xsmall" weight="semibold">
                Your postcode
              </Text>
              <Field label="your postcode" labelVisibility="hidden">
                <Controller
                  name="postcode"
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <Combobox
                      placeholder="Start typing..."
                      items={postcodeOptions}
                      onInputChange={setPostcodeInput}
                      onChange={onChange}
                      inputValue={postcodeInput}
                      value={value}
                      getOptionLabel={(option) => `${option.suburb} ${option.postCode}`}
                      getOptionValue={(option) => option.postCode}
                    />
                  )}
                />
              </Field>
            </Stack>
          ) : null}

          <Accordion type="multiple">
            <Divider />
            <Box paddingY="medium">
              <AccordionItem
                value="brands-filter"
                label="Brands"
                level="4"
                padding="none"
                justifyContent="spaceBetween"
              >
                <Stack gap="medium">
                  <Text size="xsmall" weight="semibold" id="manufacturers-selection">
                    Choose preferred manufacturers
                  </Text>
                  <Controller
                    name="manufacturerBrands"
                    control={control}
                    render={({ field: { value, onChange } }) => (
                      <MultiSelect
                        placeholder="All brands"
                        defaultOptions={value?.map((brand) => ({ label: brand, value: brand })) ?? []}
                        options={manufacturerOptions}
                        onChange={(selectedOptions) => onChange(selectedOptions[''] ?? [])}
                        aria-labelledby="manufacturers-selection"
                      />
                    )}
                  />
                </Stack>
              </AccordionItem>
            </Box>

            {promotions.length > 0 ? (
              <>
                <Divider />
                <Box paddingY="medium">
                  <AccordionItem
                    value="promotions-filter"
                    label="Offers"
                    level="4"
                    padding="none"
                    justifyContent="spaceBetween"
                  >
                    <Stack gap="medium">
                      {promotions.map((promotion, i) => (
                        <Stack gap="large" key={promotion.code}>
                          <Controller
                            name="promotions"
                            control={control}
                            render={({ field: { value, onChange } }) => (
                              <Checkbox
                                onChange={onPromotionCheckboxChange(promotion.code, value, onChange)}
                                checked={(value || []).includes(promotion.code)}
                                size="small"
                                data={{ testId: `promotion-${promotion.code}` }}
                                alignItems="center"
                              >
                                <Text>{promotion.title}</Text>
                              </Checkbox>
                            )}
                          />
                          {i !== promotions.length - 1 ? <Divider /> : null}
                        </Stack>
                      ))}
                    </Stack>
                  </AccordionItem>
                </Box>
              </>
            ) : null}

            <Divider />

            <Box paddingY="medium">
              <AccordionItem
                value="categories-filter"
                label="Categories"
                level="4"
                padding="none"
                justifyContent="spaceBetween"
              >
                <Stack gap="medium">
                  {categories?.map((category) => (
                    <Stack gap="large" key={category.name}>
                      {category.subCategories?.map((subCategory) => (
                        <Stack gap="large" key={subCategory.name}>
                          <Controller
                            name="categories"
                            control={control}
                            render={({ field: { value, onChange } }) => (
                              <Checkbox
                                onChange={onCategoryCheckboxChange(subCategory.slug, value, onChange)}
                                checked={(value || []).includes(subCategory.slug)}
                                size="small"
                                data={{ testId: `category-${subCategory.slug}` }}
                                alignItems="center"
                              >
                                <Box display="flex" flexDirection="row" alignItems="center" gap="xsmall">
                                  <img
                                    src={`${ASSET_URL}/${subCategory.slug}.svg`}
                                    alt=""
                                    height={CATEGORY_ICON_SIZE}
                                    width={CATEGORY_ICON_SIZE}
                                  />
                                  <Text>{subCategory.name}</Text>
                                </Box>
                              </Checkbox>
                            )}
                          />
                          <Divider />
                        </Stack>
                      ))}
                    </Stack>
                  ))}
                </Stack>
              </AccordionItem>
            </Box>
          </Accordion>
        </Box>

        <Stack>
          {status === 'error' ? <Alert error="GENERIC_ERROR" partner={partner as MarketplacePartner} /> : null}
          <Stack flex={1}>
            <BrighteButton loading={status === 'loading'} rounded type="submit">
              Apply filters
            </BrighteButton>
          </Stack>
        </Stack>
      </FilterSortBaseDialog>
    </>
  );
};
